/**
 * Analysis Status Hook
 *
 * Mirrors Flutter's AnalysisStatusCubit:
 * - Tracks analysis status for each trial+platform combination
 * - Polls when analysis is running/queued
 * - Auto-stops polling when completed/failed
 * - Skips re-checking if already have a final status (unless forced)
 * - Provides trigger/re-trigger functionality
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { analysisService } from '../services/analysis';
import { AnalysisStatusType } from '../types';

/** Flutter uses 10s, match that */
const POLL_INTERVAL = 10_000;

/** Composite key for trial+platform (matches Flutter's "{trialId}_{platform}") */
const statusKey = (trialId: string, platform: string) => `${trialId}_${platform}`;

/** Parsed workers status from API response */
export interface WorkersStatus {
  total: number;
  completed: number;
  failed: number;
  running: number;
}

/** Individual worker details */
export interface WorkerDetail {
  worker_id?: string;
  status: string;
  error?: string;
  started_at?: string;
  completed_at?: string;
  chunk_id?: string;
  [key: string]: any; // Allow other fields from API
}

/** Analysis status for a single trial+platform */
export interface AnalysisStatusInfo {
  status: AnalysisStatusType;
  workers?: WorkersStatus;
  workerDetails?: WorkerDetail[]; // Full worker details from API
  errorMessage?: string;
  progressPercent: number;
}

/** Parse workers array into WorkersStatus */
function parseWorkers(workers: any[] | undefined): WorkersStatus | undefined {
  if (!workers || !Array.isArray(workers)) return undefined;

  let completed = 0;
  let failed = 0;
  let running = 0;

  for (const w of workers) {
    switch (w.status) {
      case 'completed':
        completed++;
        break;
      case 'failed':
      case 'timeout':
        failed++;
        break;
      case 'running':
      case 'queued':
        running++;
        break;
    }
  }

  return { total: workers.length, completed, failed, running };
}

/** Calculate progress percentage from workers status */
function calculateProgressFromWorkers(workers: WorkersStatus | undefined): number {
  if (!workers || workers.total === 0) return 0;
  // Progress = (completed + failed) / total * 100
  // Include failed workers in progress calculation since they're "done"
  const done = workers.completed + workers.failed;
  return Math.round((done / workers.total) * 100);
}

/** Check if a status is final (not in-progress) */
function isFinalStatus(status: AnalysisStatusType): boolean {
  return status === 'COMPLETED' || status === 'FAILED' || status === 'NOT_FOUND';
}

/** Check if a status is in-progress */
function isInProgressStatus(status: AnalysisStatusType): boolean {
  return status === 'QUEUED' || status === 'RUNNING';
}

export function useAnalysisStatus() {
  const [statuses, setStatuses] = useState<Record<string, AnalysisStatusInfo>>({});
  const pollingTimers = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  /** Track pending API calls to prevent duplicates (like Flutter's _pendingRequests) */
  const pendingRequests = useRef<Set<string>>(new Set());

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(pollingTimers.current).forEach(clearInterval);
    };
  }, []);

  /** Get status for a trial+platform */
  const getStatus = useCallback(
    (trialId: string, platform: string): AnalysisStatusInfo | undefined => {
      return statuses[statusKey(trialId, platform)];
    },
    [statuses]
  );

  /** Check if analysis is in progress */
  const isInProgress = useCallback(
    (trialId: string, platform: string): boolean => {
      const info = statuses[statusKey(trialId, platform)];
      return !!info && isInProgressStatus(info.status);
    },
    [statuses]
  );

  /** Check if analysis is available (completed) */
  const isAvailable = useCallback(
    (trialId: string, platform: string): boolean => {
      const info = statuses[statusKey(trialId, platform)];
      return info?.status === 'COMPLETED';
    },
    [statuses]
  );

  /** Stop polling for a key */
  const stopPolling = useCallback((key: string) => {
    if (pollingTimers.current[key]) {
      clearInterval(pollingTimers.current[key]);
      delete pollingTimers.current[key];
    }
  }, []);

  /** Start polling for a key (mirrors Flutter's startPolling) */
  const startPolling = useCallback((key: string, trialId: string, platform: string) => {
    // Don't start if already polling
    if (pollingTimers.current[key]) return;

    pollingTimers.current[key] = setInterval(async () => {
      try {
        const response = await analysisService.getStatus(trialId, platform);
        const workers = parseWorkers(response.workers);
        const progressPercent = response.progress?.percentage ?? calculateProgressFromWorkers(workers);
        const info: AnalysisStatusInfo = {
          status: response.status,
          workers,
          workerDetails: response.workers, // Store full worker details
          errorMessage: response.error,
          progressPercent,
        };

        setStatuses((prev) => ({ ...prev, [key]: info }));

        // Stop polling if done (matches Flutter's _pollStatus)
        if (isFinalStatus(response.status)) {
          stopPolling(key);
        }
      } catch {
        // Don't stop polling on transient errors, just log (like Flutter)
        // Transient poll error - continue polling
      }
    }, POLL_INTERVAL);
  }, [stopPolling]);

  /**
   * Check status once (and start/stop polling accordingly)
   *
   * Mirrors Flutter's checkStatus():
   * - Skips if already have a final status (unless forced)
   * - Skips if a request is already in flight
   * - Auto-starts polling if in progress
   * - Stops polling if complete/failed
   */
  const checkStatus = useCallback(
    async (trialId: string, platform: string, force: boolean = false) => {
      const key = statusKey(trialId, platform);

      // Skip if we already have a final status and not forced (matches Flutter)
      if (!force) {
        const existingInfo = statuses[key];
        if (existingInfo && isFinalStatus(existingInfo.status)) {
          return;
        }
      }

      // Skip if a request is already in flight (matches Flutter's _pendingRequests)
      if (pendingRequests.current.has(key)) return;
      pendingRequests.current.add(key);

      try {
        const response = await analysisService.getStatus(trialId, platform);
        const workers = parseWorkers(response.workers);
        const progressPercent = response.progress?.percentage ?? calculateProgressFromWorkers(workers);
        const info: AnalysisStatusInfo = {
          status: response.status,
          workers,
          workerDetails: response.workers, // Store full worker details
          errorMessage: response.error,
          progressPercent,
        };

        setStatuses((prev) => ({ ...prev, [key]: info }));

        // Auto-start polling if in progress, stop if final (matches Flutter)
        if (isInProgressStatus(response.status)) {
          startPolling(key, trialId, platform);
        } else {
          stopPolling(key);
        }
      } catch {
        // If check fails, mark as unknown
        setStatuses((prev) => ({
          ...prev,
          [key]: { status: 'NOT_FOUND', progressPercent: 0 },
        }));
      } finally {
        pendingRequests.current.delete(key);
      }
    },
    [statuses, startPolling, stopPolling]
  );

  /** Trigger analysis (with optional force) and start polling */
  const triggerAnalysis = useCallback(
    async (trialId: string, platform: string, force: boolean = false) => {
      const key = statusKey(trialId, platform);

      // Set status to queued immediately for responsive UI
      setStatuses((prev) => ({
        ...prev,
        [key]: { status: 'QUEUED', progressPercent: 0 },
      }));

      try {
        await analysisService.triggerAnalysis(trialId, platform, force);

        // Clear any existing timer and start fresh polling
        stopPolling(key);
        startPolling(key, trialId, platform);
      } catch (error: any) {
        setStatuses((prev) => ({
          ...prev,
          [key]: {
            status: 'FAILED',
            progressPercent: 0,
            errorMessage: error?.message || 'Failed to trigger analysis',
          },
        }));
      }
    },
    [startPolling, stopPolling]
  );

  /** Reset all statuses (e.g., after trial list refresh) */
  const resetChecks = useCallback(() => {
    Object.values(pollingTimers.current).forEach(clearInterval);
    pollingTimers.current = {};
    pendingRequests.current.clear();
    setStatuses({});
  }, []);

  /**
   * Set status for a single trial+platform to loading (e.g. after protocol replace).
   * Does not clear other statuses. Call checkStatus(trialId, platform) after to update with real status.
   */
  const setStatusLoading = useCallback((trialId: string, platform: string) => {
    const key = statusKey(trialId, platform);
    setStatuses((prev) => ({
      ...prev,
      [key]: { status: 'RUNNING', progressPercent: 0 },
    }));
  }, []);

  return {
    statuses,
    getStatus,
    isInProgress,
    isAvailable,
    checkStatus,
    triggerAnalysis,
    resetChecks,
    setStatusLoading,
  };
}
